import React, { useState } from 'react';
import Modal from './Modal';
import { vehiclesApi } from '../api/client';

interface AddVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVehicleAdded: () => void;
}

const AddVehicleModal: React.FC<AddVehicleModalProps> = ({ isOpen, onClose, onVehicleAdded }) => {
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    vin: '',
    licensePlate: '',
    mileage: '',
    fuelType: 'gasoline' as 'gasoline' | 'diesel' | 'electric' | 'hybrid' | 'plug-in-hybrid',
    transmission: 'automatic' as 'automatic' | 'manual' | 'cvt',
    insuranceProvider: '',
    insurancePolicyNumber: '',
    insuranceExpiration: '',
    registrationState: '',
    registrationExpiration: '',
    purchaseDate: '',
    purchasePrice: '',
    dealer: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const resetForm = () => {
    setFormData({
      make: '',
      model: '',
      year: new Date().getFullYear(),
      color: '',
      vin: '',
      licensePlate: '',
      mileage: '',
      fuelType: 'gasoline',
      transmission: 'automatic',
      insuranceProvider: '',
      insurancePolicyNumber: '',
      insuranceExpiration: '',
      registrationState: '',
      registrationExpiration: '',
      purchaseDate: '',
      purchasePrice: '',
      dealer: '',
      notes: ''
    });
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.make.trim()) {
      setError('Vehicle make is required');
      return;
    }

    if (!formData.model.trim()) {
      setError('Vehicle model is required');
      return;
    }

    if (formData.year < 1900 || formData.year > new Date().getFullYear() + 1) {
      setError('Please enter a valid year');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await vehiclesApi.createVehicle({
        make: formData.make,
        model: formData.model,
        year: formData.year,
        color: formData.color || undefined,
        vin: formData.vin || undefined,
        licensePlate: formData.licensePlate || undefined,
        mileage: formData.mileage ? parseInt(formData.mileage) : undefined,
        fuelType: formData.fuelType,
        transmission: formData.transmission,
        insurance: (formData.insuranceProvider && formData.insurancePolicyNumber) ? {
          provider: formData.insuranceProvider,
          policyNumber: formData.insurancePolicyNumber,
          expirationDate: formData.insuranceExpiration
        } : undefined,
        registration: (formData.registrationState && formData.registrationExpiration) ? {
          state: formData.registrationState,
          expirationDate: formData.registrationExpiration
        } : undefined,
        purchaseInfo: (formData.purchaseDate || formData.purchasePrice) ? {
          date: formData.purchaseDate,
          price: formData.purchasePrice ? parseFloat(formData.purchasePrice) : 0,
          dealer: formData.dealer || undefined
        } : undefined,
        notes: formData.notes || undefined
      });

      setSuccess(true);
      setTimeout(() => {
        onVehicleAdded();
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error adding vehicle:', err);
      setError('Failed to add vehicle. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New Vehicle">
      <form onSubmit={handleSubmit} className="modal-form">
        {error && (
          <div className="modal-error">
            {error}
          </div>
        )}

        {success && (
          <div className="modal-success">
            Vehicle added successfully!
          </div>
        )}

        {/* Basic Information */}
        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>Make *</label>
            <input
              type="text"
              value={formData.make}
              onChange={(e) => setFormData({ ...formData, make: e.target.value })}
              placeholder="Toyota, Honda, Ford..."
              disabled={loading}
            />
          </div>

          <div className="modal-form-group">
            <label>Model *</label>
            <input
              type="text"
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              placeholder="Camry, Civic, F-150..."
              disabled={loading}
            />
          </div>
        </div>

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>Year *</label>
            <input
              type="number"
              min="1900"
              max={new Date().getFullYear() + 1}
              value={formData.year}
              onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) || new Date().getFullYear() })}
              disabled={loading}
            />
          </div>

          <div className="modal-form-group">
            <label>Color</label>
            <input
              type="text"
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              placeholder="Black, White, Red..."
              disabled={loading}
            />
          </div>
        </div>

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>License Plate</label>
            <input
              type="text"
              value={formData.licensePlate}
              onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value })}
              placeholder="ABC-1234"
              disabled={loading}
            />
          </div>

          <div className="modal-form-group">
            <label>Mileage</label>
            <input
              type="number"
              value={formData.mileage}
              onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
              placeholder="50000"
              disabled={loading}
            />
          </div>
        </div>

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>Fuel Type</label>
            <select
              value={formData.fuelType}
              onChange={(e) => setFormData({ ...formData, fuelType: e.target.value as any })}
              disabled={loading}
            >
              <option value="gasoline">Gasoline</option>
              <option value="diesel">Diesel</option>
              <option value="electric">Electric</option>
              <option value="hybrid">Hybrid</option>
              <option value="plug-in-hybrid">Plug-in Hybrid</option>
            </select>
          </div>

          <div className="modal-form-group">
            <label>Transmission</label>
            <select
              value={formData.transmission}
              onChange={(e) => setFormData({ ...formData, transmission: e.target.value as any })}
              disabled={loading}
            >
              <option value="automatic">Automatic</option>
              <option value="manual">Manual</option>
              <option value="cvt">CVT</option>
            </select>
          </div>
        </div>

        <div className="modal-form-group">
          <label>VIN</label>
          <input
            type="text"
            value={formData.vin}
            onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
            placeholder="1HGBH41JXMN109186"
            disabled={loading}
          />
        </div>

        {/* Insurance Information */}
        <h4 style={{ margin: '1rem 0 0.5rem 0', color: '#666' }}>Insurance Information</h4>
        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>Insurance Provider</label>
            <input
              type="text"
              value={formData.insuranceProvider}
              onChange={(e) => setFormData({ ...formData, insuranceProvider: e.target.value })}
              placeholder="State Farm, Geico..."
              disabled={loading}
            />
          </div>

          <div className="modal-form-group">
            <label>Policy Number</label>
            <input
              type="text"
              value={formData.insurancePolicyNumber}
              onChange={(e) => setFormData({ ...formData, insurancePolicyNumber: e.target.value })}
              placeholder="Policy number"
              disabled={loading}
            />
          </div>
        </div>

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>Insurance Expiration</label>
            <input
              type="date"
              value={formData.insuranceExpiration}
              onChange={(e) => setFormData({ ...formData, insuranceExpiration: e.target.value })}
              disabled={loading}
            />
          </div>

          <div className="modal-form-group">
            <label>Registration State</label>
            <input
              type="text"
              value={formData.registrationState}
              onChange={(e) => setFormData({ ...formData, registrationState: e.target.value })}
              placeholder="CA, NY, TX..."
              disabled={loading}
            />
          </div>
        </div>

        <div className="modal-form-group">
          <label>Registration Expiration</label>
          <input
            type="date"
            value={formData.registrationExpiration}
            onChange={(e) => setFormData({ ...formData, registrationExpiration: e.target.value })}
            disabled={loading}
          />
        </div>

        <div className="modal-form-group">
          <label>Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Additional notes about the vehicle..."
            disabled={loading}
            rows={2}
          />
        </div>

        <div className="modal-form-actions">
          <button
            type="button"
            onClick={handleClose}
            className="modal-btn modal-btn-cancel"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="modal-btn modal-btn-primary"
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add Vehicle'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddVehicleModal;